import React from 'react';
import noop from 'lodash/noop';
import { EngagementSpotlight } from '@atlassiansox/engagekit-ts';
import Button from '@atlaskit/button';
import Heading from '@atlaskit/heading';
import CrossIcon from '@atlaskit/icon/core/migration/cross';
import Image from '@atlaskit/image';
import { SpotlightTarget } from '@atlaskit/onboarding';
import { Box, Inline, Stack, Text, xcss } from '@atlaskit/primitives';
import Spinner from '@atlaskit/spinner';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { FormattedMessage, useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics, FireScreenAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { TRANSITION_HOME_BUTTON_SPOTLIGHT_NAME } from '../../../common/constants/index.tsx';
import AlertsIcon from '../../../common/ui/assets/step-alerts.svg';
import AutomateAlertsAndIncidentsResponseIcon from '../../../common/ui/assets/step-automate-alert-and-incident-response.svg';
import ChatToolsIcon from '../../../common/ui/assets/step-chat-tools.svg';
import DeprecatedOnesIcon from '../../../common/ui/assets/step-deprecated-ones.svg';
import FindIcon from '../../../common/ui/assets/step-find-what-you-are-looking-for.svg';
import Heartbeats from '../../../common/ui/assets/step-heartbeats.svg';
import IncidentsIcon from '../../../common/ui/assets/step-incidents.svg';
import IntegrationsIcon from '../../../common/ui/assets/step-integrations.svg';
import NotificationsIcon from '../../../common/ui/assets/step-notifications.svg';
import PermissionsIcon from '../../../common/ui/assets/step-permissions.svg';
import TeamsIcon from '../../../common/ui/assets/step-teams-concept.svg';
import { messages as stepMessages } from '../../../common/ui/transition-home/step/messages.tsx';
import { useProgressStore } from '../../../controllers/store/progress/index.tsx';
import { ProgressContents } from '../../../controllers/store/progress/types.tsx';
import { useOgConsolidationUserState } from '../../../controllers/use-og-consolidation-user-state/index.tsx';
import FloatingButtonDefaultIcon from './assets/left-icon.svg';
import messages from './messages.tsx';
import type { TransitionHomeButtonProps } from './types.tsx';

export const TransitionHomeButton = ({ onClick, isLoading }: TransitionHomeButtonProps) => {
	const { formatMessage } = useIntl();
	const { update } = useOgConsolidationUserState();
	const [{ activeContent }] = useProgressStore();
	const {
		src: floatingButtonSrc,
		xcssOverrides,
		stepDescription,
	} = floatingButtonAttributes[activeContent];

	return (
		<>
			<SpotlightTarget name={TRANSITION_HOME_BUTTON_SPOTLIGHT_NAME}>
				<FireScreenAnalytics actionSubjectId="eoc-changeboarding_transition_home_lozenge" />
				<Box xcss={buttonWrapperBoxStyles}>
					{/* eslint-disable-next-line @atlassian/a11y/interactive-element-not-keyboard-focusable */}
					<Box
						xcss={outerBoxStyles}
						// Replace with lodash/noop
						// eslint-disable-next-line @typescript-eslint/no-empty-function
						onClick={!isLoading ? onClick : () => {}}
						role="button"
					>
						<Inline grow="fill" alignInline="start" alignBlock="center" space="space.100">
							<Inline
								xcss={[iconWrapperInlineStyles, ...xcssOverrides]}
								alignBlock="center"
								alignInline="center"
							>
								{isLoading ? <Spinner /> : <Image src={floatingButtonSrc} width={24} alt="" />}
							</Inline>
							<Stack>
								<Heading
									size={stepDescription ? 'xxsmall' : 'xsmall'}
									as="h2"
									color="color.text.inverse"
								>
									<FormattedMessage {...messages.getStartedNonFinal} />
								</Heading>
								{stepDescription && (
									<Text color="color.text.inverse" maxLines={1}>
										<FormattedMessage {...stepDescription} />
									</Text>
								)}
							</Stack>
						</Inline>
					</Box>
					<Box xcss={closeButtonBoxStyles}>
						<Tooltip content={formatMessage(messages.hideTransitionHomeNonFinal)}>
							<Inline>
								<Button
									aria-label={formatMessage(messages.hideTransitionHomeNonFinal)}
									spacing="none"
									onClick={(e, analyticEvent) => {
										e.stopPropagation();

										fireUIAnalytics(analyticEvent, 'dismissTransitionHomeButton');

										// If an error occurs, do nothing
										update({ isTransitionHomeHidden: true }).catch(noop);
									}}
									iconBefore={
										<CrossIcon
											label={formatMessage(messages.hideTransitionHomeNonFinal)}
											color={token('color.icon.inverse')}
											LEGACY_size="small"
										/>
									}
									appearance="subtle"
								/>
							</Inline>
						</Tooltip>
					</Box>
				</Box>
			</SpotlightTarget>
			<EngagementSpotlight engagementId={TRANSITION_HOME_BUTTON_SPOTLIGHT_NAME} />
		</>
	);
};

const closeButtonBoxStyles = xcss({
	position: 'absolute',
	right: 'space.250',
	top: 'space.250',
});

const buttonWrapperBoxStyles = xcss({
	position: 'relative',
	width: '340px',
});

const outerBoxStyles = xcss({
	backgroundColor: 'color.background.accent.blue.bolder',
	padding: 'space.100',
	borderRadius: 'border.radius.circle',
	color: 'color.text.inverse',
	boxShadow: 'elevation.shadow.overlay',
	cursor: 'pointer',
	boxSizing: 'border-box',
	paddingRight: 'space.600',
	width: '100%',
});

const iconWrapperInlineStyles = xcss({
	position: 'relative',
	flexShrink: 0,
	width: 'size.400',
	height: 'size.400',
	borderRadius: 'border.radius.circle',
	background: `${token('color.background.accent.blue.subtlest')}`,
	border: `2px solid ${token('color.icon.inverse')}`,
	overflow: 'hidden',
});

const orangeBackgroundStyles = xcss({ backgroundColor: 'color.background.accent.orange.subtler' });
const blueBackgroundStyles = xcss({ backgroundColor: 'color.background.accent.blue.subtler' });
const redSubtlerBackgroundStyles = xcss({ backgroundColor: 'color.background.accent.red.subtler' });
const redSubtlestBackgroundStyles = xcss({
	backgroundColor: 'color.background.accent.red.subtlest',
});
const yellowBackgroundStyles = xcss({ backgroundColor: 'color.background.accent.yellow.subtler' });
const yellowWarningBackgroundStyles = xcss({ backgroundColor: 'color.background.warning' });
const blueSublestBackgroundStyles = xcss({
	backgroundColor: 'color.background.accent.blue.subtlest',
});
const opaqSublestBackgroundStyles = xcss({
	backgroundColor: 'color.background.accent.gray.subtler',
});

const floatingButtonAttributes = {
	[ProgressContents.Home]: {
		src: FloatingButtonDefaultIcon,
		xcssOverrides: [],
		stepDescription: undefined,
	},
	[ProgressContents.UsersAndRoles]: {
		src: PermissionsIcon,
		xcssOverrides: [blueBackgroundStyles],
		stepDescription: stepMessages.usersAndRolesTitleNonFinal,
	},
	[ProgressContents.Alerts]: {
		src: AlertsIcon,
		xcssOverrides: [orangeBackgroundStyles],
		stepDescription: stepMessages.alertsAndOnCallTitleNonFinal,
	},
	[ProgressContents.Notifications]: {
		src: NotificationsIcon,
		xcssOverrides: [redSubtlerBackgroundStyles],
		stepDescription: stepMessages.notificationsTitleNonFinal,
	},
	[ProgressContents.Teams]: {
		src: TeamsIcon,
		xcssOverrides: [yellowBackgroundStyles],
		stepDescription: stepMessages.teamsTitleNonFinal,
	},
	[ProgressContents.ChatTools]: {
		src: ChatToolsIcon,
		xcssOverrides: [blueSublestBackgroundStyles],
		stepDescription: stepMessages.chatToolsTitleNonFinal,
	},
	[ProgressContents.AutomateAlertAndIncidentResponse]: {
		src: AutomateAlertsAndIncidentsResponseIcon,
		xcssOverrides: [yellowWarningBackgroundStyles],
		stepDescription: stepMessages.automateAlertAndIncidentResponseTitleNonFinal,
	},
	[ProgressContents.Integrations]: {
		src: IntegrationsIcon,
		xcssOverrides: [opaqSublestBackgroundStyles],
		stepDescription: stepMessages.integrationsTitleNonFinal,
	},
	[ProgressContents.Find]: {
		src: FindIcon,
		xcssOverrides: [yellowBackgroundStyles],
		stepDescription: stepMessages.findWhatYouAreLookingForTitleNonFinal,
	},
	[ProgressContents.DeprecatedOnes]: {
		src: DeprecatedOnesIcon,
		xcssOverrides: [redSubtlestBackgroundStyles],
		stepDescription: stepMessages.deprecatedOnesTitleNonFinal,
	},
	[ProgressContents.Incidents]: {
		src: IncidentsIcon,
		xcssOverrides: [yellowBackgroundStyles],
		stepDescription: stepMessages.incidentsTitleNonFinal,
	},
	[ProgressContents.Heartbeats]: {
		src: Heartbeats,
		xcssOverrides: [blueSublestBackgroundStyles],
		stepDescription: stepMessages.heartbeatsTitleNonFinal,
	},
};
